import type { NumberCondition } from "@skilitics/judy";
import { isReferencedValue } from "@skilitics/nautilus-core-designer-data";

import type { ConditionBuilder, ConditionBuilderContext } from "../../types";
import { isNumberOperator, isTernaryOperator } from "./helpers";
import type { NumericConditionEditorProps, NumericConditionValues } from "./specifiers/NumericConditionSpecifier";
import { NumericConditionEditor, NumericConditionSummary } from "./specifiers/NumericConditionSpecifier";

// note: the "offset" is used to allow the ui range differ from the stored
//       range, for example, visually indexing from 1 but internally from 0

export interface NumberConditionBuilderContext extends ConditionBuilderContext {
  config: NumericConditionValues;
  spec: Omit<NumericConditionEditorProps, keyof NumericConditionValues | "onChange" | "values"> & { property: string };

  // the assumption is that any property referenced by consuming node designer is a number
  result: Record<string, number>;
}

export type NumberConditionBuilder = typeof numberConditionBuilder;

// generic so asks for a property name in spec
export const numberConditionBuilder: ConditionBuilder<NumberConditionBuilderContext> = {
  typeId: "number",
  defaultCategory: "Variables",
  build: ({ config, spec: { property }, resultProperty, resolvableValue }) => {
    if (!numberConditionBuilder.isValid?.(config)) return;

    const { operator, value, value2 } = config;

    const condition = {
      type: "number",
      subject: resultProperty(property, "number"),
      operator,
    };

    if (isTernaryOperator(operator)) {
      if (value2 === undefined) return;

      const left = resolvableValue(value, "number");
      const right = resolvableValue(value2, "number");
      if (left === undefined || right === undefined) return;

      return { ...condition, left, right } as NumberCondition;
    }

    const object = resolvableValue(value, "number");
    if (object === undefined) return;

    return { ...condition, object } as NumberCondition;
  },
  isValid: ({ operator, value }) => isNumberOperator(operator) && (Number.isFinite(value) || isReferencedValue(value)),
  getInitialConfig: () => ({}),
  views: {
    summary: ({ spec: { title, offset, formatStringOverrides }, config }) => (
      <NumericConditionSummary
        title={title}
        offset={offset}
        values={config}
        formatStringOverrides={formatStringOverrides}
      />
    ),
    edit: ({ spec: { title, min, max, formatStringOverrides, offset }, config, onConfigChange }) => (
      <NumericConditionEditor
        title={title}
        min={min}
        max={max}
        formatStringOverrides={formatStringOverrides}
        values={config}
        onChange={onConfigChange}
        offset={offset}
      />
    ),
  },
};
